import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.1.0-alpha';

function normChange(x){
  x=x||{};
  return Object.freeze({
    evidenceId:x.evidenceId||null,
    change:['WITHDRAWN','STALE','CONFLICTED','REPLACED','REFRESHED','UNKNOWN'].includes(x.change)?x.change:'UNKNOWN',
    replacementId:x.replacementId||null,
    verified:x.verified===true
  });
}

function actionFor(change){
  const c=normChange(change);
  if(c.change==='WITHDRAWN') return 'INVALIDATE';
  if(c.change==='CONFLICTED') return 'REVIEW_CONFLICT';
  if(c.change==='STALE') return 'REVIEW_EVIDENCE';
  if(c.change==='REPLACED') return c.verified?'REVALIDATE':'REVIEW_EVIDENCE';
  if(c.change==='REFRESHED') return c.verified?'REVALIDATE':'REVIEW_EVIDENCE';
  return 'REVIEW_EVIDENCE';
}

function plan(graph,changes){
  const dep=global.ZooInvestEvidenceDependencyGraphEngine;
  if(!dep) throw new Error('Evidence Dependency Graph Engine required');
  const rows=(Array.isArray(changes)?changes:[]).map(normChange);
  const byConclusion=new Map();

  for(const ch of rows){
    if(!ch.evidenceId) continue;
    const affected=dep.affectedConclusions(graph,[ch.evidenceId]);
    for(const c of affected){
      const action=actionFor(ch);
      if(!byConclusion.has(c.id)) byConclusion.set(c.id,{
        conclusionId:c.id,label:c.label||null,actions:[],evidenceIds:[]
      });
      const rec=byConclusion.get(c.id);
      rec.actions.push(action);
      rec.evidenceIds.push(ch.evidenceId);
    }
  }

  const priority={INVALIDATE:4,REVIEW_CONFLICT:3,REVIEW_EVIDENCE:2,REVALIDATE:1};
  const conclusions=[...byConclusion.values()].map(r=>{
    const finalAction=r.actions.sort((a,b)=>priority[b]-priority[a])[0];
    return Object.freeze({
      conclusionId:r.conclusionId,
      label:r.label,
      action:finalAction,
      evidenceIds:Object.freeze([...new Set(r.evidenceIds)])
    });
  });

  return Object.freeze({
    conclusions:Object.freeze(conclusions),
    affectedCount:conclusions.length,
    unaffectedCount:Math.max(0,graph.nodes.filter(n=>n.type==='CONCLUSION').length-conclusions.length),
    wholeCompanyInvalidated:false,
    companyQualityOverride:false,
    entryGateBypass:false,
    tradeSignal:false,
    capitalPermission:false
  });
}

function explain(result){
  const r=result||{conclusions:[]};
  return Object.freeze((r.conclusions||[]).map(x=>{
    const text=x.action==='INVALIDATE'?'Dependent evidence was withdrawn; this conclusion is invalidated until rebuilt.':
      x.action==='REVIEW_CONFLICT'?'Dependent evidence is conflicted; preserve the contradiction and review.':
      x.action==='REVALIDATE'?'Evidence was refreshed/replaced with verified support; revalidate this conclusion.':
      'Dependent evidence changed or is stale; review this conclusion only.';
    return Object.freeze({conclusionId:x.conclusionId,action:x.action,text});
  }));
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const dep=global.ZooInvestEvidenceDependencyGraphEngine;
  const g=dep.graph([
    {id:'e1',type:'EVIDENCE',status:'VALID'},
    {id:'e2',type:'EVIDENCE',status:'VALID'},
    {id:'c1',type:'CONCLUSION',label:'Demand'},
    {id:'c2',type:'CONCLUSION',label:'Thesis'},
    {id:'c3',type:'CONCLUSION',label:'Balance sheet'}
  ],[{from:'e1',to:'c1'},{from:'c1',to:'c2'},{from:'e2',to:'c3'}]);

  let p=plan(g,[{evidenceId:'e1',change:'WITHDRAWN'}]);
  ok('withdrawal affects direct conclusion',p.conclusions.some(x=>x.conclusionId==='c1'));
  ok('withdrawal affects downstream conclusion',p.conclusions.some(x=>x.conclusionId==='c2'));
  ok('unrelated conclusion remains unaffected',!p.conclusions.some(x=>x.conclusionId==='c3'));
  ok('withdrawal invalidates dependent conclusion',p.conclusions.every(x=>x.action==='INVALIDATE'));
  ok('unaffected count preserved',p.unaffectedCount===1);

  p=plan(g,[{evidenceId:'e1',change:'STALE'}]);
  ok('stale evidence requests scoped review',p.conclusions.every(x=>x.action==='REVIEW_EVIDENCE'));

  p=plan(g,[{evidenceId:'e1',change:'CONFLICTED'}]);
  ok('conflict stays explicit',p.conclusions.every(x=>x.action==='REVIEW_CONFLICT'));

  p=plan(g,[{evidenceId:'e1',change:'REFRESHED',verified:true}]);
  ok('verified refresh requests revalidation',p.conclusions.every(x=>x.action==='REVALIDATE'));

  p=plan(g,[{evidenceId:'e1',change:'REFRESHED',verified:false}]);
  ok('unverified refresh cannot revalidate',p.conclusions.every(x=>x.action==='REVIEW_EVIDENCE'));

  p=plan(g,[{evidenceId:'e1',change:'STALE'},{evidenceId:'e1',change:'WITHDRAWN'}]);
  ok('strongest action wins',p.conclusions.every(x=>x.action==='INVALIDATE'));

  ok('never invalidates whole company automatically',p.wholeCompanyInvalidated===false);
  ok('never overrides company quality',p.companyQualityOverride===false);
  ok('never bypasses entry gate',p.entryGateBypass===false);
  ok('emits no trade signal',p.tradeSignal===false);
  ok('grants no capital permission',p.capitalPermission===false);
  ok('explanation generated',explain(p).length===2);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,normChange,actionFor,plan,explain,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestConclusionInvalidationEngine=Object.freeze(API);
})(engineContext);